import { useRef } from 'react';
import { coupleMonogram, type CoupleNameParts } from '@vendorops/shared';
import { useCouplePhoto } from '@/hooks/useCouplePhoto';
import CouplePhoto from '@/components/CouplePhoto';
import CouplePhotoTarget from '@/components/CouplePhotoTarget';

/**
 * The couple's portrait on a wedding that exists, as a one-click way to set it.
 *
 * Saves on pick, like the edit dialog's field and for the same reason: the
 * picture changes in front of the planner, so it has already been saved. There
 * is no Save button anywhere near this one to disagree with.
 *
 * **No remove.** Clearing the photo is a destructive edit to the wedding, and
 * the edit dialog is the screen that owns those — `CouplePhotoField` has the
 * control. A header portrait that could be emptied by a stray click on the
 * wrong menu item would be the cheap version of this.
 */
export default function CouplePhotoButton({
  weddingId,
  couple,
  photoKey,
  className,
}: {
  weddingId: string;
  couple: CoupleNameParts;
  /** The stored photo's key, or null. Doubles as the cache-buster: it gets a
   *  fresh uuid per upload, so it changes exactly when the bytes do. */
  photoKey: string | null;
  className?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { pick, uploading } = useCouplePhoto(weddingId);

  return (
    <>
      <CouplePhotoTarget
        size="sm"
        label={photoKey ? 'Replace the couple photo' : 'Add a couple photo'}
        busy={uploading}
        onClick={() => inputRef.current?.click()}
        className={className}
      >
        <CouplePhoto
          weddingId={weddingId}
          hasPhoto={Boolean(photoKey)}
          monogram={coupleMonogram(couple)}
          version={photoKey ?? 0}
          // The heading right beside it names the couple, so an alt would be
          // read out twice for one picture. The button carries the name.
          alt=""
        />
      </CouplePhotoTarget>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        onChange={(e) => {
          pick(e.target.files?.[0]);
          // Cleared so picking the *same* file again after a failure still
          // fires a change event.
          e.target.value = '';
        }}
      />
    </>
  );
}
